// /managers/ConvoManager/ConvoManager.jsx
import React, { createContext, useContext, useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query as firestoreQuery, orderBy as firestoreOrderBy, limit as firestoreLimit, onSnapshot } from 'firebase/firestore';
import { authManager } from '@/managers/AuthManager';
import { botManager } from '@/managers/BotManager';
import { keyStateManager } from '@/managers/zKeysManager';
import { getBotRuntimeSystemMessage } from '@/utils/defaultBotProfile';
import { ConversationState } from './ConvoState';
import { createConvoActions, EMPTY_CONVO_ID } from './ConvoActions';

const ConversationContext = createContext(null);
const SIDEBAR_CONVERSATION_LIMIT = 50;

// --- ARCHITECTURAL IMPROVEMENT ---
// The enrichment logic is now a pure function outside the hook.
// This prevents it from causing re-renders and makes it testable.
const getEnrichedConversation = (baseConversation, botState) => {
  if (!baseConversation) {
    return null;
  }

  const liveBot = botState.allBots.find(b => b.id === baseConversation.botId);
  const liveModel = botState.allPlatformModels.find(m => m.id === baseConversation.modelId);

  // Start with a copy of the base conversation to avoid mutation.
  const finalConversation = { ...baseConversation };
  
  if (liveBot) {
    finalConversation.botName = liveBot.name;
    finalConversation.customSystemMessage = getBotRuntimeSystemMessage(liveBot);
  }
  
  if (liveModel) {
    finalConversation.modelName = liveModel.name;
    finalConversation.botPlatform = liveModel.platform;
    finalConversation.botModel = liveModel.model;
    finalConversation.botCleanModelName = liveModel.cleanModelName || liveModel.model;
    finalConversation.modelMaxTokens = liveModel.maxTokens || null;
    if (finalConversation.temperature === undefined) {
       finalConversation.temperature = liveModel.temperatureRange ? (liveModel.temperatureRange[0] + liveModel.temperatureRange[1]) / 2 : 0.7;
    }
  }
  
  return finalConversation;
};


export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within a ConvoManager');
  }

  // --- REVISED LOGIC ---
  // The useConversation hook is now a lean consumer. 
  // It derives the enriched conversation from the stable context value.
  const enrichedCurrentConversation = useMemo(() => {
    return getEnrichedConversation(context.currentConversation, context.botState);
  }, [context.currentConversation, context.botState]);
  
  // The final return value is memoized to prevent downstream re-renders.
  const finalReturnValue = useMemo(() => ({
    ...context,
    currentConversation: enrichedCurrentConversation,
  }), [context, enrichedCurrentConversation]);

  return finalReturnValue;
};

export const ConvoManager = ({ children }) => {
  const navigate = useNavigate();
  const firestore = getFirestore();
  
  // --- STATE CONSOLIDATION ---
  // The main ConversationState instance, created once.
  const [state] = useState(() => new ConversationState());

  // The React state snapshot that drives re-renders.
  const [stateSnapshot, setStateSnapshot] = useState(() => state.getPublicState());
  
  // BOT STATE is now managed here, in the provider, not in the hook.
  const [botState, setBotState] = useState(() => botManager.getState());

  const activeMetaListenersRef = useRef({});
  const activeMessageListenersRef = useRef({});
  const lastMessageStatesRef = useRef(new Map()); 

  // --- EFFECT: Subscribe to ConversationState changes ---
  useEffect(() => {
    const unsubscribe = state.subscribe(setStateSnapshot);
    return unsubscribe;
  }, [state]);

  // --- EFFECT: Subscribe to BotManager changes ---
  // This is now done ONCE in the provider.
  useEffect(() => {
    const unsubBot = botManager.subscribe(setBotState);
    return unsubBot;
  }, []);

  // --- ACTIONS CREATION ---
  // Memoized actions, stable as long as state and navigate are stable.
  const actions = useMemo(() => {
    return createConvoActions(state, navigate, activeMetaListenersRef, activeMessageListenersRef, lastMessageStatesRef);
  }, [state, navigate]);

  // --- EFFECT: Upgrade EMPTY_CONVO_ID when activeBotConfig becomes available ---
  // Handles the race where createConversation fires before keys are loaded.
  // Once activeBotConfig transitions null → non-null, if the app is stuck on the
  // placeholder, create a real conversation automatically.
  const prevActiveBotConfigRef = useRef(null);
  useEffect(() => {
    const prevConfig = prevActiveBotConfigRef.current;
    prevActiveBotConfigRef.current = botState.activeBotConfig;
    if (!prevConfig && botState.activeBotConfig && stateSnapshot.currentConversationId === EMPTY_CONVO_ID) {
      actions.createConversation();
    }
  }, [botState.activeBotConfig, stateSnapshot.currentConversationId, actions]);

  // --- EFFECT: App Ready State ---
  useEffect(() => {
    const updateReadyState = () => {
      const authState = authManager.getState();
      const currentBotState = botManager.getState(); // Get latest bot state
      const ready = authState.isInitialized && !authState.isAuthenticating && currentBotState.isInitialized && !currentBotState.isLoading;
      if (state.isAppReady !== ready) {
        state.setIsAppReady(ready);
      }
    };
    updateReadyState();
    const unsubAuth = authManager.subscribe(updateReadyState);
    const unsubBot = botManager.subscribe(updateReadyState);
    return () => { unsubAuth(); unsubBot(); };
  }, [state]);

  // --- EFFECT: Conversation List Listener ---
  useEffect(() => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid || !stateSnapshot.isAppReady) {
      if (Object.keys(state.conversationsMeta).length > 0) {
        state.updateConversationsMeta({});
        state.setCurrentConversationId(null);
      }
      return;
    }

    const q = firestoreQuery(
      collection(firestore, 'users', currentAuthUser.uid, 'conversations'),
      firestoreOrderBy('lastUpdated', 'desc'),
      firestoreLimit(SIDEBAR_CONVERSATION_LIMIT)
    );

    const unsubscribeConvoList = onSnapshot(q, (snapshot) => {
      const updates = {};
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          updates[change.doc.id] = { ...change.doc.data(), id: change.doc.id, persisted: true };
        } else if (change.type === 'removed') {
          updates[change.doc.id] = null;
        }
      });
      state.updateConversationsMeta(prev => {
        const next = { ...prev };
        Object.entries(updates).forEach(([id, data]) => {
          if (data === null) delete next[id];
          else next[id] = { ...(next[id] || {}), ...data };
        });
        return next;
      });
      if (!state.isConvoListListenerAttached) state.setIsConvoListListenerAttached(true);
    }, (err) => {
      console.error('[ConvoManager] Error fetching conversation list:', err);
      state.setError(`Failed to load conversation list: ${err.message}`);
    });

    return () => {
      unsubscribeConvoList();
      state.setIsConvoListListenerAttached(false);
    };
  }, [stateSnapshot.isAppReady, firestore, state]);

  // --- EFFECT: Active Conversation Listeners (Meta & Messages) ---
  useEffect(() => {
    const activeConvoId = stateSnapshot.currentConversationId;
    if (!activeConvoId) {
      return; 
    }

    const convoMeta = stateSnapshot.conversations[activeConvoId];
    if (convoMeta?.persisted) {
      actions.setupMetaListener(activeConvoId);
      if (convoMeta.messagesLoaded) {
        actions.setupMessageListener(activeConvoId);
      }
    }
    
    return () => {
      actions.cleanupMetaListener(activeConvoId);
      actions.cleanupMessageListener(activeConvoId);
      lastMessageStatesRef.current.delete(activeConvoId);
    };
  }, [stateSnapshot.currentConversationId, stateSnapshot.conversations[stateSnapshot.currentConversationId]?.messagesLoaded, actions]);

  // --- STABLE CALLBACKS for context ---
  const setManifestForConversation = useCallback((id, manifest, handle) => {
    return actions.updateConversation(id, { parManifest: { manifest, handle } });
  }, [actions]);

  const clearManifestForConversation = useCallback((id) => {
    if (id && state.conversationsMeta[id]?.parManifest !== null) {
      actions.updateConversation(id, { parManifest: null });
    }
  }, [actions, state.conversationsMeta]);
  
  // --- CONTEXT VALUE CREATION ---
  // This is the single source of truth for all consumers.
  const contextValue = useMemo(() => {
    const authState = authManager.getState();
    if (!authState.user && !authState.isAuthenticating && stateSnapshot.isAppReady) {
      // Return a stable, minimal context for unauthenticated users.
      return {
        conversations: {}, messages: {}, currentConversationId: null,
        currentConversation: null, optimisticMessages: [], error: stateSnapshot.error,
        setError: (error) => state.setError(error), isInitialized: stateSnapshot.isAppReady,
        isConversationLoading: () => false, apiKeys: {}, defaultAIMemorySize: 10,
        inputEstimation: stateSnapshot.inputEstimation,
        updateInputEstimation: (estimation) => state.setInputEstimation(estimation),
        isLoadingOlderConversations: false, isNavigating: false,
        createConversation: async () => ({ success: false, error: 'User not authenticated', id: null }),
        sendUserMessage: async () => ({ success: false, error: 'User not authenticated' }),
        botState, // Provide botState even when logged out
      };
    }

    return {
      ...stateSnapshot,
      ...actions,
      botState, // Add botState directly to the context
      setError: (error) => state.setError(error),
      updateInputEstimation: (estimation) => state.setInputEstimation(estimation),
      isInitialized: stateSnapshot.isAppReady,
      isConversationLoading: (convoId) => state.isConversationLoading(convoId),
      getConversationById: (id) => stateSnapshot.conversations[id],
      getManifestForConversation: (id) => state.getManifestForConversation(id),
      clearPendingMessage: (conversationId) => state.clearPendingMessage(conversationId),
      setManifestForConversation,
      clearManifestForConversation,
      clearRewoundMessagePayload: () => state.clearRewoundMessagePayload(),
    };
  }, [stateSnapshot, actions, state, botState, setManifestForConversation, clearManifestForConversation]);

  return (
    <ConversationContext.Provider value={contextValue}>
      {children}
    </ConversationContext.Provider>
  );
};

export default ConvoManager;